<template>
	<li
		v-if="rowHandler"
		:key="rowHandler.key"
		class="cart-list-row"
	>
		<div class="cart-list-product-wrap">
			<product-link
				class="image-link"
				:product="row.product"
			>
				<ximg
					:src="convertToCdnUrl(productImage)"
					:ariaLabel="row.product.name"
					lazy
					role="img"
				/>
			</product-link>

			<div class="product-title-wrap">
				<fixed-lines
					:useEllipsis="false"
					:lines="1"
					lineHeight="1.2em"
					class="product-list-item-title"
				>
					<product-link
						:product="row.product"
						class="secondary-link"
					>
						{{ row.product.name }}
					</product-link>
				</fixed-lines>

				<fixed-lines
					v-if="row.product.subTitle"
					class="sub-title"
					:useEllipsis="false"
					:lines="1"
					lineHeight="1.2em"
				>
					{{ row.product.subTitle }}
				</fixed-lines>
				<a
					v-if="previewUrl"
					:href="previewUrl"
					target="_blank"
					class="product-id"
				>
					{{ t('PRODUCT.PREVIEW') }}
				</a>
				<span
					v-else-if="row.product.ifsId"
					class="sub-title product-id"
				>
					{{ t('PRODUCT.CODE',row.product.id) }} ({{ row.product.ifsId }})
				</span>
				<span
					v-else
					class="sub-title product-id"
				>
					{{ t('PRODUCT.CODE',row.product.id) }}
				</span>
			</div>
			<template v-if="row.product.discontinued && ! inStock">
				<div class="alert alert-danger">
					Produkten har utgått
				</div>
				<icon-button
					v-if="! rowHandler.static"
					@click="remove"
					variant="delete"
					size="tiny"
				/>
			</template>
			<template v-else-if="fullyBooked">
				<div class="alert alert-info">
					Tillfälligt fullbokad
				</div>
				<icon-button
					v-if="! rowHandler.static"
					@click="remove"
					variant="remove"
					size="tiny"
				/>
			</template>
			<template v-else>
				<div class="cart-price-qty">
					<product-price
						right
						:product="row.product"
						:priceOverride="row.unitPrice"
						:priceOverrideVat="row.product.regularPrice.vat/row.product.regularPrice.price"
						:subscriptionProduct="subscriptionProduct"
					/>
					<div class="cart-item-quantity">
						<template v-if="! subscriptionProduct">
							<div v-if="rowHandler.static" class="text-right semibold">
								{{ rowHandler.quantity }} st
							</div>
							<input-number
								v-else
								@mincapped="attractFocusToDeleteButton"
								:min="1"
								:max="250"
								size="tiny"
								v-model="rowHandler.quantity"
								@input="(ev) => quantityWatch(ev)"
							/>
						</template>
						<icon-button
							v-if="! rowHandler.static"
							@click="remove"
							variant="remove"
							size="tiny"
						/>
					</div>
				</div>
				<div
					v-if="computedStock"
					class="stock-modal"
				>
					<stock-row
						v-if="computedStock.download"
						:product="row.product"
						:stock="computedStock"
						store="download"
						size="small"
					/>
					<template v-else>
						<stock-row
							:product="row.product"
							:name="t('STORE.WEB_STOCK')"
							:stock="computedStock"
							:release="row.product.release"
							store="web"
							size="small"
						/>
						<hr
							v-if="storeSuggestions.favourites.length"
							class="starred-stores-hr m-0"
						>
						<stock-row
							v-for="store in storeSuggestions.favourites"
							:key="store"
							:product="row.product"
							:store="store"
							:stock="computedStock"
							:release="row.product.release"
							size="small"
						/>
						<hr
							v-if="storeSuggestions.suggestions.length"
							class="m-0"
						>
						<stock-row
							v-for="store in storeSuggestions.suggestions"
							:key="store"
							:product="row.product"
							:store="store"
							:stock="computedStock"
							:release="row.product.release"
							size="small"
						/>
					</template>
				</div>
			</template>
		</div>
		<template v-if="subscriptionProduct">
			<addition
				:rowHandler="rowHandler"
				:additions="row.extraData.additionalProducts"
				:monthlyCost="row.extraData.monthlyCost"
			/>
		</template>
		<template v-if="! subscriptionProduct">
			<div v-if="showInsurance" class="product-list-compact-wrap insurance-selector">
				<insurance-selector
					class="_compact"
					:rowHandler="rowHandler"
					:insurance="rowHandler.row.insurance"
					:locked="rowHandler.static"
				/>
			</div>
		</template>
		<store-pickup-only
			v-if="row.product.isShippable === false"
			:text="t('PRODUCT.STORE_PICKUP_ONLY_SHORT')"
			:light="true"
		/>
		<mail-order-only
			v-if="row.product.isShippable === true && row.product.isCollectable === false"
			:text="t('PRODUCT.MAIL_ORDER_ONLY_SHORT')"
			:light="true"
		/>
		<hr>
	</li>
</template>

<script>
import alert from 'alert';
import bus from 'eventbus';
import store from 'datastore';
import { mapState } from 'vuex';
import { t } from 'datastore/i18n';
import * as staticData from 'staticData';
import { inStockAnywhere, formatPrice, getStoreSuggestions, convertToCdnUrl } from 'utils';

export default {
	store,
	data () {
		return {
			row: this.rowHandler && this.rowHandler.row,
			additions: [],
			monthlyCost: '',
		};
	},
	props: {
		rowHandler: {
			type: Object,
			required: false,
		},
		currency: {
			type: String,
			required: true,
		},
		small: {
			type: Boolean,
			default: false,
		},
		stockMap: {
			type: Object,
			default: null,
		},
		stock: {
			type: [Object, Array],
			default: null,
		},
		canAddInsurance: {
			type: Boolean,
			default: false,
		},
		isCustomConfig: {
			type: Boolean,
		}
	},
	methods: {
		t,
		tc: t.prefix('CART'),
		convertToCdnUrl,
		formatPrice,
		inStockAnywhere,
		attractFocusToDeleteButton () {
			alert('Ta bort produkter från kundvagn med kryss-knappen');
			// @TODO
		},
		quantityWatch (ev) {
			this.rowHandler.update(ev);
 			if (ev === 0) {
		    	bus.$emit('product-page.remove-from-cart');
			}
		},
		remove () {
			this.$emit('remove', this.rowHandler);
		},
	},
	computed: {
		previewUrl () {
			if (this.row.product.id === staticData.giftCardProductId) {
				const ed = this.row.extraData;
				const data = {
					value: this.row.unitPrice,
					message: ed.message,
					theme: ed.theme.id,
					buyerName: ed.buyerName,
				};
				const parameters = [];

				for (const key in data) {
					if (data.hasOwnProperty(key)) {
						parameters.push(key + '=' + encodeURIComponent(data[key]));
					}
				}

				return '/api/gift-card/preview?' + parameters.join('&');
			}
			return null;
		},
		productImage () {
			if (this.row.product.id === staticData.giftCardProductId) { //Presentkort
				return this.row.extraData.theme.image;
			}
			return '/images/product/' + this.row.product.id + '?trim&h=80';
		},
		storeSuggestions () {
			return getStoreSuggestions(this.computedStock, this.user.storeSuggestions, this.userSettings.myStores, 4);
		},
		computedStock () {
			if (this.stock) {
				return this.stock;
			}
			if (! this.stockMap) {
				return null;
			}
			return this.stockMap[this.row.product.id + ''];
		},
		inStock () {
			if (this.row.product.stock) {
				return inStockAnywhere(this.row.product.stock);
			}
			return false;
		},
		path () {
			return this.$router.route({
				name: 'product',
				params: {
					id: this.row.product.id,
					name: this.row.product.mainTitle,
				},
			});
		},
		showInsurance () {
			if(this.isCustomConfig) {
				return false;
			}
			if (this.rowHandler.static && (! this.row.extraData || ! this.row.extraData.insured)) {
				return false;
			}
			return this.productHasInsurance && this.canAddInsurance;
		},
		productHasInsurance () {
			return this.row.insurance && this.row.insurance.price;
		},
		subscriptionProduct () {
			if (this.row.extraData && this.row.extraData.source) {
				return this.row.extraData.source == "verticula";
			}
			return false;
		},
		fullyBooked () {
			return this.row.product.statusCodes && this.row.product.statusCodes.indexOf(9/* Tillfälligt fullbokad*/) >= 0;
		},
		...mapState({
			user: state => state.user,
			userSettings: state => state.userSettings,
		}),
	},
	watch: {
		rowHandler (to) {
			this.row = to.row;
		},
	},
};
</script>
